import { useState, type JSX } from 'react';
import {
  CAMERA_TRANSPORTS,
  CAMERA_VENDORS,
  type Camera,
  type CameraTransport,
  type CameraVendor,
  type NetworkNode,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { CollapsibleSection } from '../../shared/ui/CollapsibleSection';
import { formatMoment } from '../../shared/format/time';
import { ArchiveSettings } from '../archive/ArchiveSettings';
import { NodeCredentials } from '../vnc/NodeCredentials';
import { useCameras, useCreateCamera, useProbeCamera, useUpdateCamera } from './api';
import { describeExtracted, parseRtspInput } from './rtsp-input';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';

const VENDOR_LABEL_KEYS: Readonly<Record<CameraVendor, string>> = {
  generic: 'video.vendor.generic',
  hikvision: 'video.vendor.hikvision',
  dahua: 'video.vendor.dahua',
  axis: 'video.vendor.axis',
  onvif: 'video.vendor.onvif',
};

const TRANSPORT_LABEL_KEYS: Readonly<Record<CameraTransport, string>> = {
  tcp: 'video.transport.tcp',
  udp: 'video.transport.udp',
};

/** Что сказать, когда учётных данных RTSP ещё не завели. */
const NO_RTSP_CREDENTIALS = 'credentials.noRtspHint';

/**
 * Камера в панели узла (§11, фаза 7, задачи 3 и 10).
 *
 * Отсюда камеру заводят, зондируют и открывают на стене. Отдельного экрана
 * «камеры» нет намеренно: камера — свойство узла, и инженер, у которого
 * не идёт поток, должен видеть про этот узел всё в одном месте — и адрес,
 * и мониторы, и путь RTSP.
 */
export function CameraSection({
  node,
  open,
  onToggle,
  canView,
  canWrite,
  canArchive,
  onOpen,
  onOpenArchive,
}: {
  node: NetworkNode;
  /** Раскрыт ли раздел. Решает это панель: раскрытым может быть только один. */
  open: boolean;
  onToggle: () => void;
  canView: boolean;
  canWrite: boolean;
  canArchive: boolean;
  onOpen: (cameraId: string) => void;
  onOpenArchive: (cameraId: string) => void;
}): JSX.Element | null {
  const { t } = useTranslation();

  const cameras = useCameras(canView);
  const camera = cameras.data?.find((item) => item.nodeId === node.id) ?? null;

  if (node.kind !== 'camera') {
    return null;
  }

  return (
    <CollapsibleSection
      title={t('video.title')}
      icon="navVideo"
      /* Камеры ещё нет — сворачивать нечего: внутри одна форма создания,
         и за шевроном она была бы спрятана вместе с единственным способом
         камеру завести. */
      open={camera === null ? null : open}
      onToggle={onToggle}
      actions={
        camera === null ? null : (
          <div className="flex items-center gap-1.5">
            {/*
              Кнопка архива видна только тому, у кого есть право §7.2
              `camera:archive`, и только когда способ доступа задан:
              звать в раздел, который сразу скажет «не настроено», —
              это отправить человека за отказом.
            */}
            {canArchive && camera.archiveProvider !== null ? (
              <Button
                size="sm"
                variant="secondary"
                icon="layers"
                disabled={!camera.isEnabled}
                onClick={() => onOpenArchive(camera.id)}
              >
                {t('nav.archive')}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              icon="expand"
              disabled={!canView || !camera.isEnabled}
              title={camera.isEnabled ? undefined : t('video.cameraDisabled')}
              onClick={() => onOpen(camera.id)}
            >
              {t('video.toWall')}
            </Button>
          </div>
        )
      }
    >
      {camera === null ? (
        /* Тот же ключ и здесь: набранный для одного узла путь не должен
           достаться другому — заведут камеру не с тем потоком. */
        <NewCamera
          key={node.id}
          node={node}
          canWrite={canWrite}
          // Заведённая камера тут же показывает свои настройки: до создания
          // раздел был несворачиваемым, и закрыться на ровном месте он
          // выглядел бы как потеря только что набранного.
          onCreated={() => {
            if (!open) {
              onToggle();
            }
          }}
        />
      ) : (
        <>
          <ProbeSummary camera={camera} />
          {/*
            Ключ по камере, а не по порядку в разметке: обе формы набирают
            поля из своей камеры один раз, при появлении. Без ключа переход
            к соседней камере оставлял бы на экране прежние путь, порт
            и настройки архива — а сохранение записало бы их новой камере.
          */}
          <div key={camera.id} className="flex flex-col gap-2.5">
            <CameraForm camera={camera} canWrite={canWrite} />
            <ArchiveSettings camera={camera} canWrite={canWrite} />
            <NodeCredentials nodeId={node.id} service="rtsp" emptyHint={NO_RTSP_CREDENTIALS} />
          </div>
        </>
      )}
    </CollapsibleSection>
  );
}

/**
 * Поле пути RTSP, принимающее и ссылку целиком.
 *
 * В поле ждут путь, а вставляют ссылку — в том виде, в каком её дал
 * производитель камеры. Отвергать такой ввод значит требовать от человека
 * разобрать ссылку самому; вместо этого путь вынимается, а про остальное
 * поле говорит, куда это положить.
 */
function RtspPathField({
  id,
  label,
  value,
  placeholder,
  disabled,
  nodeAddress,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  nodeAddress: string | null;
  onChange: (value: string) => void;
}): JSX.Element {
  const { t } = useTranslation();

  const [hint, setHint] = useState('');

  return (
    <>
      <label className="text-dim" htmlFor={id}>
        {label}
      </label>
      <div className="flex flex-col gap-1">
        <input
          id={id}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          className="h-7 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
          onChange={(event) => {
            const parsed = parseRtspInput(event.target.value);

            setHint(describeExtracted(parsed, nodeAddress, t));
            onChange(parsed.path);
          }}
        />
        {hint === '' ? null : <p className="text-[11px] leading-snug text-warn">{hint}</p>}
      </div>
    </>
  );
}

/** Короткая сводка того, что известно о потоке. */
function ProbeSummary({ camera }: { camera: Camera }): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  const probe = useProbeCamera();

  const codec =
    camera.codecHint === null
      ? t('video.codecUnknown')
      : camera.codecHint === 'h265'
        ? t('video.codecH265Warning')
        : camera.codecHint;

  return (
    <div className="flex flex-col gap-1.5">
      <dl className="grid grid-cols-[86px_1fr] gap-x-3 gap-y-1 font-mono text-xs">
        <dt className="text-dim">{t('video.stream')}</dt>
        <dd className="truncate" title={camera.mainPath}>
          {camera.mainPath}
        </dd>
        <dt className="text-dim">Substream</dt>
        <dd className={camera.subPath === null ? 'text-warn' : ''}>
          {camera.subPath ?? t('video.noSubstream')}
        </dd>
        <dt className="text-dim">{t('video.codecLabel')}</dt>
        <dd className={camera.codecHint === 'h265' ? 'text-warn' : ''}>{codec}</dd>
        <dt className="text-dim">{t('video.resolution')}</dt>
        <dd>
          {camera.probedWidth === null || camera.probedHeight === null
            ? t('video.resolutionUnknown')
            : `${camera.probedWidth}×${camera.probedHeight}`}
        </dd>
        <dt className="text-dim">{t('video.probe')}</dt>
        <dd className={camera.lastProbeError === null ? '' : 'text-down-fg'}>
          {camera.lastProbeAt === null
            ? t('video.notProbed')
            : camera.lastProbeError === null
              ? formatMoment(camera.lastProbeAt, language)
              : messageText(camera.lastProbeError, t)}
        </dd>
      </dl>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          icon="refresh"
          disabled={probe.isPending}
          onClick={() => probe.mutate(camera.id)}
        >
          {probe.isPending ? t('agents.probe.testing') : t('video.probeStream')}
        </Button>
        {camera.hasCredential ? null : (
          <span className="text-[11px] text-dim">{t('video.noRtspCredential')}</span>
        )}
      </div>
    </div>
  );
}

/** Заведение камеры на узле, у которого её ещё нет. */
function NewCamera({
  node,
  canWrite,
  onCreated,
}: {
  node: NetworkNode;
  canWrite: boolean;
  onCreated: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const create = useCreateCamera();
  const [mainPath, setMainPath] = useState('/Streaming/Channels/101');

  if (!canWrite) {
    return <p className="text-xs text-dim">{t('video.noCameraForbidden')}</p>;
  }

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate({ nodeId: node.id, mainPath }, { onSuccess: onCreated });
      }}
    >
      <p className="text-xs leading-snug text-muted">{t('video.newCameraHint')}</p>

      <div className="grid grid-cols-[86px_1fr] items-start gap-x-3 gap-y-2 text-sm">
        <RtspPathField
          id={`cam-new-${node.id}`}
          label={t('video.stream')}
          value={mainPath}
          placeholder="/Streaming/Channels/101"
          nodeAddress={node.address}
          onChange={setMainPath}
        />
      </div>

      {create.error === null ? null : (
        <p className="text-[11px] text-down-fg">{errorText(create.error)}</p>
      )}

      <Button size="sm" variant="secondary" icon="plus" type="submit" disabled={create.isPending}>
        {t('video.createCamera')}
      </Button>
    </form>
  );
}

/** Параметры заведённой камеры. */
function CameraForm({ camera, canWrite }: { camera: Camera; canWrite: boolean }): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const update = useUpdateCamera(camera.id);

  const [mainPath, setMainPath] = useState(camera.mainPath);
  const [subPath, setSubPath] = useState(camera.subPath ?? '');
  const [port, setPort] = useState(String(camera.port));
  const [vendor, setVendor] = useState<CameraVendor>(camera.vendor);
  const [transport, setTransport] = useState<CameraTransport>(camera.transport);
  const [isEnabled, setIsEnabled] = useState(camera.isEnabled);

  return (
    <form
      className="grid grid-cols-[86px_1fr] items-start gap-x-3 gap-y-2 text-sm"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({
          mainPath,
          subPath: subPath.trim().length === 0 ? null : subPath.trim(),
          port: Number(port),
          vendor,
          transport,
          isEnabled,
        });
      }}
    >
      <RtspPathField
        id={`cam-main-${camera.id}`}
        label={t('video.stream')}
        value={mainPath}
        disabled={!canWrite}
        nodeAddress={camera.nodeAddress}
        onChange={setMainPath}
      />

      <RtspPathField
        id={`cam-sub-${camera.id}`}
        label="Substream"
        value={subPath}
        placeholder={t('video.notSet')}
        disabled={!canWrite}
        nodeAddress={camera.nodeAddress}
        onChange={setSubPath}
      />

      <label className="text-dim" htmlFor={`cam-port-${camera.id}`}>
        {t('agents.probe.port')}
      </label>
      <input
        id={`cam-port-${camera.id}`}
        value={port}
        disabled={!canWrite}
        inputMode="numeric"
        onChange={(event) => setPort(event.target.value)}
        className="h-7 w-24 rounded-xs border border-line-strong bg-surface px-2 font-mono text-xs"
      />

      <label className="text-dim" htmlFor={`cam-vendor-${camera.id}`}>
        {t('nodes.field.kind')}
      </label>
      <select
        id={`cam-vendor-${camera.id}`}
        value={vendor}
        disabled={!canWrite}
        onChange={(event) => setVendor(event.target.value as CameraVendor)}
        className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
      >
        {CAMERA_VENDORS.map((value) => (
          <option key={value} value={value}>
            {t(VENDOR_LABEL_KEYS[value])}
          </option>
        ))}
      </select>

      <label className="text-dim" htmlFor={`cam-transport-${camera.id}`}>
        {t('video.transportLabel')}
      </label>
      <select
        id={`cam-transport-${camera.id}`}
        value={transport}
        disabled={!canWrite}
        onChange={(event) => setTransport(event.target.value as CameraTransport)}
        className="h-7 rounded-xs border border-line-strong bg-surface px-1.5 text-xs"
      >
        {CAMERA_TRANSPORTS.map((value) => (
          <option key={value} value={value}>
            {t(TRANSPORT_LABEL_KEYS[value])}
          </option>
        ))}
      </select>

      <span className="text-dim">{t('video.enabled')}</span>
      <label className="flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={isEnabled}
          disabled={!canWrite}
          onChange={(event) => setIsEnabled(event.target.checked)}
        />
        {t('video.disabledHint')}
      </label>

      {update.error === null ? null : (
        <p className="col-span-2 text-[11px] text-down-fg">{errorText(update.error)}</p>
      )}

      {canWrite ? (
        <Button
          size="sm"
          variant="secondary"
          icon="check"
          type="submit"
          className="col-start-2"
          disabled={update.isPending}
        >
          {t('common.action.save')}
        </Button>
      ) : null}
    </form>
  );
}
